import { supabase } from '@/integrations/supabase/client';

export interface ModuleProgress {
  module_id: string;
  completed: boolean;
  completed_at?: string;
  quiz_score?: number;
}

export const markModuleComplete = async (userId: string, moduleId: string) => {
  const query: any = (supabase as any)
    .from('user_progress')
    .upsert({
      user_id: userId,
      module_id: moduleId,
      completed: true,
      completed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'user_id,module_id'
    })
    .select()
    .single();

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getModuleProgress = async (userId: string, moduleId: string) => {
  const query: any = (supabase as any)
    .from('user_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('module_id', moduleId)
    .maybeSingle();

  const { data, error } = await query;
  if (error) throw error;
  return data;
};

export const getAllUserProgress = async (userId: string) => {
  const query: any = (supabase as any)
    .from('user_progress')
    .select('*')
    .eq('user_id', userId);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const calculateOverallProgress = async (userId: string): Promise<{ totalModules: number; completedModules: number; percentage: number }> => {
  try {
    // Using any to bypass type recursion issue until types are regenerated
    const modulesQuery: any = (supabase as any).from('modules').select('id').eq('is_deprecated', false);
    const progressQuery: any = (supabase as any).from('user_progress').select('module_id').eq('user_id', userId).eq('completed', true);
    
    const [modulesResult, progressResult] = await Promise.all([modulesQuery, progressQuery]);
    
    const totalModules = modulesResult.data?.length || 16;
    const completedModules = progressResult.data?.length || 0;

    return {
      totalModules,
      completedModules,
      percentage: Math.round((completedModules / totalModules) * 100),
    };
  } catch (error) {
    console.error('Error calculating progress:', error);
    return { totalModules: 16, completedModules: 0, percentage: 0 };
  }
};

export const checkMasteryGate = async (userId: string) => {
  // Get quiz scores for modules 1-8
  const { data: attempts } = await supabase
    .from('quiz_attempts')
    .select('score, quiz_id, passed')
    .eq('user_id', userId)
    .eq('passed', true);

  if (!attempts || attempts.length === 0) {
    return { passed: false, averageScore: 0, requiredScore: 80 };
  }

  // Get best score for each quiz
  const quizScores = new Map<string, number>();
  attempts.forEach((attempt) => {
    const currentBest = quizScores.get(attempt.quiz_id) || 0;
    if (attempt.score > currentBest) {
      quizScores.set(attempt.quiz_id, attempt.score);
    }
  });

  const scores = Array.from(quizScores.values());
  const averageScore = scores.length > 0
    ? scores.reduce((sum, score) => sum + score, 0) / scores.length
    : 0;

  return {
    passed: averageScore >= 80 && scores.length >= 8,
    averageScore: Math.round(averageScore),
    requiredScore: 80,
    completedQuizzes: scores.length,
  };
};

export const getNextModule = async (userId: string) => {
  // Get all modules - using any to bypass type recursion
  const modulesQuery: any = (supabase as any).from('modules').select('*').eq('is_deprecated', false).order('module_order');
  const progressQuery: any = (supabase as any).from('user_progress').select('module_id').eq('user_id', userId).eq('completed', true);
  
  const [modulesResult, progressResult] = await Promise.all([modulesQuery, progressQuery]);
  const modules = modulesResult.data;
  const progress = progressResult.data;

  if (!modules || modules.length === 0) return null;

  const completedModuleIds = new Set(progress?.map((p: any) => p.module_id) || []);

  // Find first incomplete module
  const nextModule = modules.find((module: any) => !completedModuleIds.has(module.id));

  // Check mastery gate if trying to access module 9+
  if (nextModule && nextModule.module_order > 8) {
    const masteryGate = await checkMasteryGate(userId);
    if (!masteryGate.passed) {
      return {
        ...nextModule,
        locked: true,
        lockReason: 'mastery_gate',
        masteryGateInfo: masteryGate,
      };
    }
  }

  return nextModule;
};

export const canAccessModule = async (userId: string, moduleOrder: number) => {
  // Modules 1-4 always accessible
  if (moduleOrder <= 4) return { canAccess: true };

  // For modules 5-8, need previous module completed
  if (moduleOrder <= 8) {
    const modulesQuery: any = (supabase as any).from('modules').select('id').eq('module_order', moduleOrder - 1).eq('is_deprecated', false).single();
    const modulesResult = await modulesQuery;
    const modules = modulesResult.data;

    if (!modules) return { canAccess: false, reason: 'Previous module not found' };

    const progressQuery: any = (supabase as any).from('user_progress').select('completed').eq('user_id', userId).eq('module_id', modules.id).eq('completed', true).maybeSingle();
    const progressResult = await progressQuery;
    const progress = progressResult.data;

    return {
      canAccess: !!progress,
      reason: progress ? undefined : 'Complete previous module first',
    };
  }

  // For modules 9+, need mastery gate
  const masteryGate = await checkMasteryGate(userId);
  if (!masteryGate.passed) {
    return {
      canAccess: false,
      reason: `Mastery gate: Need 80%+ average on modules 1-8 (current: ${masteryGate.averageScore}%)`,
      masteryGateInfo: masteryGate,
    };
  }

  // Also need previous module completed
  const modulesQuery: any = (supabase as any).from('modules').select('id').eq('module_order', moduleOrder - 1).eq('is_deprecated', false).single();
  const modulesResult = await modulesQuery;
  const modules = modulesResult.data;

  if (!modules) return { canAccess: false, reason: 'Previous module not found' };

  const progressQuery: any = (supabase as any).from('user_progress').select('completed').eq('user_id', userId).eq('module_id', modules.id).eq('completed', true).maybeSingle();
  const progressResult = await progressQuery;
  const progress = progressResult.data;

  return {
    canAccess: !!progress,
    reason: progress ? undefined : 'Complete previous module first',
  };
};
